/* eslint-disable eqeqeq */
/* eslint-disable react/no-array-index-key */
import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import ChevronIcon from "../../Common/Svgs/ChevronIcon";
import CustomTooltip from "../../Core/Components/CustomTooltip";
import { ChargerFillIcon } from "../../Common/Svgs/ChargerIcon";
import InfoCircleIcon from "../../Common/Svgs/InfoCircleIcon";
import { updateHub } from "../../Redux/Slices/hubs.slice";

const ENERGY_STATUS = {
  UNAVAILABLE: -1,
  ACCESS: 1,
  SHORTFALL: 2,
};

const getColors = (percentage, status) => {
  // if any one of the energy is not available return green-10 color
  if (percentage === 0) {
    return {
      primaryColor: "#085054",
      primary10Color: "#E6EDEE",
      primaryColorClass: "text-green",
      borderClass: "hover:border-green",
    };
  }

  // if available energy is more than required energy then return green color
  if (status === ENERGY_STATUS.ACCESS) {
    return {
      primaryColor: "#085054",
      primary10Color: "#E6EDEE",
      primaryColorClass: "text-green",
      borderClass: "hover:border-green",
    };
  }

  // if shortfall is greater than 25%, return red color
  if (status === ENERGY_STATUS.SHORTFALL && percentage > 0.25) {
    return {
      primaryColor: "#C84630",
      primary10Color: "#F9ECEA",
      primaryColorClass: "text-alert-red",
      borderClass: "hover:border-alert-red",
    };
  }

  // return orange (warning) color
  return {
    primaryColor: "#F18725",
    primary10Color: "#F1872533",
    primaryColorClass: "text-warning",
    borderClass: "hover:border-warning",
  };
};

const HubCard = ({ data }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // to render the dashes
  const dashes = Array.from({ length: 17 }, (_, idx) => idx + 1);

  const { primaryColor, primary10Color, primaryColorClass, borderClass } =
    getColors(data?.energyStatusPercent, data?.energyStatus);

  const getChargeMoreBatteries = () => {
    return data.requiredBatteries - data.chargedBatteries;
  };

  const handleClick = () => {
    dispatch(
      updateHub({
        key: "selectedHub",
        value: data,
      }),
    );

    navigate(`/hub-detail/${data.id}`);
  };

  return (
    <div
      role="button"
      className={`min-w-[424px] w-[32%] h-[195px] bg-background p-4 rounded-md border border-light-grey ${borderClass}`}
      onClick={handleClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="font-poppins text-2xl font-[250] text-dark-gray">
            <span
              className="font-semibold"
              style={{
                color:
                  data?.energyStatus == ENERGY_STATUS.ACCESS
                    ? "#000000"
                    : primaryColor,
              }}
            >
              {data?.chargedBatteries ?? "--"}
            </span>
            /{data?.totalBatteries ?? "--"}
          </p>
        </div>

        <CustomTooltip tooltipTitle="Energy available">
          <div
            className="py-1 px-2 flex items-center gap-x-2 rounded-md border-[0.5px]"
            style={{
              borderColor: primaryColor,
            }}
          >
            <ChargerFillIcon className={primaryColorClass} />
            <p
              className="font-poppins text-xs font-medium"
              style={{ color: primaryColor }}
            >
              {data?.totalBatteryCapacity
                ? `${data.totalBatteryCapacity} kWh`
                : "--"}
            </p>
          </div>
        </CustomTooltip>
      </div>

      <div className="mt-1 flex items-center justify-between">
        <p className="font-aileron text-xs font-normal text-dark-gray">
          Batteries Charged
        </p>

        {data?.energyStatus != ENERGY_STATUS.ACCESS && (
          <div className="flex items-center gap-x-2 mr-2">
            <InfoCircleIcon className={primaryColorClass} />
            <p
              className="font-aileron font-normal text-xs capitalize"
              style={{ color: primaryColor }}
            >
              Charge {getChargeMoreBatteries()} more batteries
            </p>
          </div>
        )}
      </div>

      <div className="mt-2 flex items-center justify-between">
        <p className="font-poppins text-base font-medium capitalize">
          {data?.name}
        </p>

        <ChevronIcon className="w-4 h-4 stroke-black" />
      </div>

      <div className="mt-2 flex items-center gap-x-1">
        {dashes.map((item, index) => {
          return (
            <div
              key={`dash-${item}-${index}`}
              className={`flex-1 h-2
                ${index === 0 && "rounded-l-full"}
                ${index === dashes.length - 1 && "rounded-r-full"}
                `}
              style={{
                backgroundColor: index < 14 ? primaryColor : primary10Color,
              }}
            />
          );
        })}
      </div>

      <div className="mt-2 flex items-center justify-between">
        <p className="font-aileron text-xs font-normal text-dark-gray capitalize">
          Upcoming Trips
        </p>

        <div
          className="h-5 px-[8.5px] flex items-center rounded-md"
          style={{ background: primary10Color }}
        >
          <p
            className="font-poppins text-xs font-medium"
            style={{ color: primaryColor }}
          >
            {data?.upcomingTrips}
          </p>
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between">
        <p className="font-aileron text-xs font-normal text-dark-gray capitalize">
          Upcoming Sessions
        </p>

        <div
          className="h-5 px-[8.5px] flex items-center rounded-md"
          style={{ background: primary10Color }}
        >
          <p
            className="font-poppins text-xs font-medium"
            style={{ color: primaryColor }}
          >
            {data?.upcomingSessions}
          </p>
        </div>
      </div>
    </div>
  );
};

export default HubCard;
